import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  /** `raised` sits on the cream surface; `flat` has no shadow. */
  variant?: 'raised' | 'flat' | 'sunken';
  padding?: 'none' | 'sm' | 'md' | 'lg';
};

export function Card({
  variant = 'raised',
  padding = 'md',
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div className={cx('card', `card--${variant}`, `card--pad-${padding}`, className)} {...rest}>
      {children}
    </div>
  );
}

export type CardHeaderProps = {
  title: ReactNode;
  /** Small line above the title — the `NOW SHOWING` treatment. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Renders the title in the display serif rather than UI type. */
  display?: boolean;
  /*
   * The same argument as `EmptyState`'s: the level is about the document
   * outline, not the size. 5 is the common case — a card inside a section that
   * already has its own heading — but a card that *is* a top-level section of
   * the page should pass 2 rather than skipping three levels under the h1.
   *
   * `display` keeps its h2 regardless: that variant is a section title by
   * definition, which is why it is set in the display serif.
   */
  headingLevel?: 2 | 3 | 4 | 5;
  className?: string;
};

export function CardHeader({
  title,
  eyebrow,
  description,
  actions,
  display = false,
  headingLevel = 5,
  className,
}: CardHeaderProps) {
  // `.card__title` pins family, size, weight and colour, so the tag changes the
  // outline and never the pixels.
  const Title = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5';

  return (
    <div className={cx('card__head', className)}>
      <div className="card__head-text">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        {display ? (
          <h2 className="card__title card__title--display">{title}</h2>
        ) : (
          <Title className="card__title">{title}</Title>
        )}
        {description ? <p className="card__desc">{description}</p> : null}
      </div>
      {actions ? <div className="card__head-actions">{actions}</div> : null}
    </div>
  );
}
